import type { Cue } from "../engine.ts";
import { TRIM } from "../levels.ts";
import { bell, brass, choir, cymbal, later, noise, snare, sub, thump, timpani, trim, tuba, wood } from "../synth.ts";
import { hz, stab } from "../theory.ts";

/**
 * Match flow as music. The setup countdown sits on the dominant (A) and the unlock resolves to
 * the tonic (D) in open fifths — nobody owns the point yet, so there's no third. Ownership adds
 * the third: major for us, the Phrygian flat second for them.
 */

/** Setup countdown, 5 → 1. The last three land on a timpani A that the unlock resolves. */
export const countdown =
  (n: number): Cue =>
  trim(n > 3 ? TRIM.match.tick : TRIM.match.drum, (o) => {
    if (n > 3) {
      wood(o, 1400, 0.5);
      return 0.2;
    }
    wood(o, 1000, 0.9);
    timpani(o, hz(-29), 0.35 + (3 - n) * 0.2);
    return 1.4;
  });

/** The objective unlocks: tonic in open fifths, timpani, a cymbal. */
export const unlock: Cue = trim(TRIM.match.unlock, (o) => {
  stab(o, [-12, -5, 0, 7], 1, 0.7);
  timpani(o, hz(-24), 1);
  cymbal(o, 0.8, 2);
  thump(o, 0.6, 80, 35, 0.4);
  bell(later(o, 0.02), hz(24), 0.5, 0.45);
  bell(later(o, 0.03), hz(31), 0.5, 0.4);
  return 2.4;
});

/** A new round is set up: two timpani strokes under a swelling cymbal. */
export const roundIntro: Cue = trim(TRIM.match.roundIntro, (o) => {
  timpani(o, hz(-24), 0.7);
  timpani(later(o, 0.22), hz(-24), 0.9);
  cymbal(later(o, 0.1), 0.5, 1.1, true);
  stab(later(o, 0.22), [-12, -5], 0.6, 0.9);
  return 1.8;
});

/** We took the point: a bright rising D major arpeggio. */
export const captured: Cue = trim(TRIM.match.captured, (o) => {
  [0, 4, 7, 12].forEach((s, i) => brass(later(o, i * 0.035), hz(s), 0.55 - i * 0.035, 0.9));
  cymbal(o, 0.55, 1.4);
  thump(o, 0.45, 100, 45, 0.25);
  bell(later(o, 0.14), hz(24), 0.4, 0.5);
  return 1.6;
});

/** They took it (from us, or from nobody): a low Phrygian cluster and a falling sweep. */
export const pointLost: Cue = trim(TRIM.match.lost, (o) => {
  stab(o, [-24, -23, -17], 0.9, 0.7, tuba);
  stab(o, [-12, -11], 0.6, 0.6);
  thump(o, 0.7, 70, 28, 0.45);
  noise(o, { color: "pink", type: "lowpass", f: 2400, f1: 200, gain: 0.35, a: 0.02, d: 0.7 });
  return 1.6;
});

/** Round won: plagal-into-authentic cadence, IV → V → I. */
export const roundWon: Cue = trim(TRIM.match.roundWon, (o) => {
  stab(o, [5, 9, 12], 0.8, 0.2);
  stab(later(o, 0.24), [7, 11, 14], 0.85, 0.2);
  stab(later(o, 0.48), [0, 7, 12, 16], 1, 1);
  sub(later(o, 0.48), hz(-24), 1, 0.7);
  timpani(later(o, 0.48), hz(-24), 0.9);
  cymbal(later(o, 0.48), 0.7, 1.8);
  return 2.6;
});

/** Round lost: the Phrygian cadence, bII → i, slow and low. */
export const roundLost: Cue = trim(TRIM.match.roundLost, (o) => {
  stab(o, [-11, -7, -4], 0.7, 0.45);
  stab(later(o, 0.5), [-12, -9, -5], 0.7, 1.2);
  stab(later(o, 0.5), [-24], 0.8, 1.2, tuba);
  timpani(later(o, 0.5), hz(-24), 0.6);
  return 2.4;
});

/** Victory fanfare: a triplet pickup on the dominant, the tonic chord, then IV → I. */
export const victory: Cue = trim(TRIM.match.victory, (o) => {
  for (let i = 0; i < 3; i++) brass(later(o, i * 0.13), hz(-5), 0.1, 0.8);
  const hit = 0.4;
  stab(later(o, hit), [-12, 0, 4, 7, 12], 1, 1.1);
  timpani(later(o, hit), hz(-24), 1);
  cymbal(later(o, hit), 0.9, 2.4);
  sub(later(o, hit), hz(-24), 1.1, 0.7);
  stab(later(o, 1.65), [-7, 5, 9, 12], 0.85, 0.35);
  stab(later(o, 2.05), [-12, 0, 7, 12, 16], 1, 1.6);
  for (const [i, s] of [24, 28, 31, 36].entries()) bell(later(o, 2.05 + i * 0.06), hz(s), 0.5, 0.5);
  timpani(later(o, 2.05), hz(-24), 1);
  cymbal(later(o, 2.05), 0.8, 2.6);
  return 4.6;
});

/** Defeat: a slow minor descent over a low pedal, choir and brass. */
export const defeat: Cue = trim(TRIM.match.defeat, (o) => {
  const steps: [number, number][] = [
    [-5, 0.5],
    [-9, 0.5],
    [-10, 0.5],
    [-12, 1.6],
  ];
  let at = 0;
  for (const [s, d] of steps) {
    choir(later(o, at), hz(s), d, 0.8);
    brass(later(o, at), hz(s - 12), d, 0.5);
    at += d * 0.95;
  }
  tuba(o, hz(-24), 3, 0.6);
  sub(o, hz(-24), 3, 0.6);
  timpani(later(o, 1.45), hz(-24), 0.6);
  snare(later(o, 1.45), 0.2);
  return 4.4;
});
